<template>
  <BaseModule
    v-bind="$props"
    class="about-us"
    data-module="about-us"
  >
    <div class="content">
      <header
        v-if="title || subtitle || text"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>
    </div>

    <div class="wrapper">
      <!-- Slider main container -->
      <div
        ref="cards-container"
        class="swiper container-cards content inner"
      >
        <!-- Additional required wrapper -->
        <div class="swiper-wrapper">
          <!-- Slides -->
          <div
            v-for="(card, idx) in cards"
            v-show="cardHasTitleOrDescription(card)"
            :key="card.id || idx"
            class="swiper-slide card"
          >
            <div class="container-border">
              <h3 class="base-content-fontsize">{{ card.cardTitle }}</h3>
              <div
                class="card-description base-content-fontsize"
                v-html="card.cardDescription"
              ></div>
            </div>
          </div>
        </div>

        <!-- Add Pagination -->
        <div class="swiper-pagination"></div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';

import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'AboutUs',
  components: {
    BaseModule,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    cards: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      swiper: null,
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.swiper = new Swiper(this.$refs['cards-container'], {
        modules: [Autoplay, Pagination],
        autoplay: !this.editing,
        observer: true,
        speed: 500,
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
        breakpoints: {
          320: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          1000: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        },
      });
    });
  },
  methods: {
    cardHasTitleOrDescription(card) {
      const hasTitle =
        typeof card.cardTitle === 'string' && /\S/.test(card.cardTitle);
      const hasDescription =
        typeof card.cardDescription === 'string' &&
        /\S/.test(card.cardDescription);

      return hasTitle || hasDescription;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
            ],
          },
          {
            name: 'columns',
            label: 'Colunas',
            fields: [
              {
                name: 'cards',
                type: 'List',
                minItems: 1,
                maxItems: 3,
                itemLabel: 'Coluna {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar coluna',
                fields: [
                  {
                    name: 'cardTitle',
                    type: 'ShortText',
                    label: 'Título',
                    maxlength: 20,
                    default: 'MISSÃO',
                  },
                  {
                    name: 'cardDescription',
                    type: 'LongText',
                    label: 'Texto de apoio',
                    default: 'Sodales sed tristique in, aliquam eu magna.',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.about-us {
  z-index: 0;
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    z-index: 2;
    line-height: 1.7em;

    .txt {
      margin: 0 auto;
      max-width: 700px;
      text-align: center;
      font-weight: $lb-medium;
      line-height: 1.7em;

      &::v-deep {
        strong {
          font-weight: $lb-bold !important;
        }

        a,
        a:visited {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }

  .wrapper {
    .container-cards {
      display: flex;
      background-color: $lb-white;

      .card {
        text-align: center;
        width: 100%;
        background-color: transparent;
        flex-grow: 1;
        position: relative;
        box-shadow: none;

        h3 {
          text-transform: uppercase;
          color: $lb-black;
          font-weight: $lb-medium;
          margin-bottom: 30px;
        }

        .container-border {
          padding: 0px 2.3em;

          .card-description {
            font-weight: $lb-medium;
            color: $lb-medium-gray;
            line-height: 1.7em;
            white-space: pre-line;
          }
        }
      }
    }

    .swiper-horizontal > .swiper-pagination-bullets {
      padding-top: 20px;
      position: initial;
    }

    &::v-deep {
      .swiper-pagination-bullet,
      .swiper-pagination-bullet-active {
        background: var(--title-color, $lb-dark-gray) !important;
      }
    }
  }
}

@include media-breakpoint-down(sm) {
  .about-us {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
    .card {
      height: 400px;
    }
  }
}

@include media-breakpoint-down(lg) {
  .about-us {
    .wrapper .container-cards {
      flex-wrap: wrap;
      height: 100%;

      .card {
        padding: 2em 0em;
        height: auto;
        min-height: auto;
      }
    }
  }
}

@include media-breakpoint-up(lg) {
  .about-us {
    .container-cards {
      padding: 4em 0;

      .card {
        height: auto;

        &:not(:first-child) {
          border-left: 1px solid #dadada;
        }
      }
    }
  }
}
</style>
